import { History, Plus, Sparkles, X } from 'lucide-react';

import { useAgentPanelContext } from '@/app/agentConversation/_context';

import { Button } from '@/app/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/ui/tooltip';
import { useAgentConversation } from '@/data/agentConversation';

/** The panel's title and its controls: a new conversation, the history, and close. */
const Header = ({ isHistoryOpen, onToggleHistory }) => {
  const panel = useAgentPanelContext();

  const handleNew = () => {
    panel.startNewConversation();
    if (isHistoryOpen) onToggleHistory();
  };

  return (
    // As tall as the record pages' heading row (PageHeading's pt-2, its
    // breadcrumb row and mb-2, plus the border), so the two bottom lines meet.
    <header className="flex h-[calc(2.75rem+1px)] shrink-0 items-center gap-1 border-b pr-2 pl-4">
      <Sparkles className="text-primary size-4 shrink-0" />
      <div className="min-w-0 flex-1 truncate text-sm font-semibold" data-testid="agent-panel-title">
        {isHistoryOpen ? 'Conversations' : <HeaderTitle />}
      </div>
      <HeaderButton label="New Conversation" icon={Plus} onClick={handleNew} disabled={panel.isRunning} testId="agent-new-conversation" />
      <HeaderButton label="Conversations" icon={History} onClick={onToggleHistory} disabled={panel.isRunning} isActive={isHistoryOpen} testId="agent-history-toggle" />
      <HeaderButton label="Close" icon={X} onClick={panel.close} testId="agent-panel-close" />
    </header>
  );
};

// The title of the conversation once it has one; until then, the assistant's name.
const HeaderTitle = () => {
  const panel = useAgentPanelContext();
  if (!panel.conversationUid) return 'Assistant';
  return <SavedConversationTitle uid={panel.conversationUid} />;
};

// Read from the conversation itself, which the chat below is already fetching:
// the history list may never have been loaded.
const SavedConversationTitle = ({ uid }) => {
  const conversation = useAgentConversation(uid);
  if (!conversation.data) return 'Assistant';
  return conversation.data.title;
};

const HeaderButton = ({ label, icon: Icon, onClick, disabled = false, isActive = false, testId }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        data-active={isActive}
        className="data-[active=true]:bg-accent"
      >
        <Icon />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

export default Header;
